import React from 'react';
import { View, Text } from 'react-native';
import Svg, {
  Circle, Text as SvgText, Image, Defs, ClipPath,
} from 'react-native-svg';
import CustomCarSVG from './CustomCarSVG';
import CustomVanSVG from './CustomVanSVG';

// Helper to get initials from player name
const getInitials = (firstName, lastName) => `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();

// Helper to get seat color based on state
const getSeatColor = (seatNumber, assignment, selectedSeat, driverSeat) => {
  if (seatNumber === driverSeat) {
    return assignment ? '#2E7D32' : '#4CAF50'; // Darker green for driver
  }
  if (selectedSeat === seatNumber) {
    return '#2196F3'; // Blue for selected
  }
  if (assignment) {
    return '#4CAF50'; // Green for assigned
  }
  return 'grey'; // Grey for available
};

function CarSVG({
  seats, seatAssignments, onSeatPress, color, selectedSeat, driverSeat,
}) {
  const seatLayout = [
    { cx: 200, cy: 400 }, { cx: 400, cy: 400 }, // Front seats
    { cx: 200, cy: 550 }, { cx: 300, cy: 550 }, { cx: 400, cy: 550 }, // Rear seats
  ];

  const seatConfigs = {
    1: [seatLayout[1]],
    2: [seatLayout[1], seatLayout[4]],
    3: [seatLayout[1], seatLayout[4], seatLayout[2]],
    4: [seatLayout[1], seatLayout[4], seatLayout[2], seatLayout[3]],
  };

  const seatCoordinates = seatConfigs[seats] || seatLayout.slice(0, seats);
  const radius = 35;

  return (
    <Svg width="170" height="220" viewBox="0 0 595.279 841.891">
      <Defs>
        {seatCoordinates.map((coord, index) => (
          <ClipPath key={`clip-${index}`} id={`clip-car-${index}`}>
            <Circle cx={coord.cx} cy={coord.cy} r={radius} />
          </ClipPath>
        ))}
      </Defs>
      <CustomCarSVG color={color} />
      {seatCoordinates.map((coord, index) => {
        const seatNumber = index + 1;
        const assignment = seatAssignments?.find((a) => a.seatNumber === seatNumber);
        const isDriverSeat = seatNumber === driverSeat;
        const seatColor = getSeatColor(seatNumber, assignment, selectedSeat, driverSeat);
        const hasAvatar = assignment?.player?.profile?.pictureThumbnail;
        const isCompanion = !isDriverSeat && assignment?.isCompanion;

        return (
          <React.Fragment key={index}>
            <Circle
              cx={coord.cx}
              cy={coord.cy}
              r={radius}
              fill={seatColor}
              onPress={() => onSeatPress(seatNumber, assignment)}
            />
            {hasAvatar && !isDriverSeat ? (
              <Image
                x={coord.cx - radius}
                y={coord.cy - radius}
                width={radius * 2}
                height={radius * 2}
                href={assignment.player.profile.pictureThumbnail}
                clipPath={`url(#clip-car-${index})`}
                preserveAspectRatio="xMidYMid slice"
                onPress={() => onSeatPress(seatNumber, assignment)}
              />
            ) : assignment ? (
              <SvgText
                x={coord.cx}
                y={coord.cy}
                fontSize="20"
                fill="white"
                textAnchor="middle"
                alignmentBaseline="central"
                fontWeight="bold"
              >
                {isDriverSeat
                  ? 'D'
                  : getInitials(assignment.player.firstName, assignment.player.lastName)}
              </SvgText>
            ) : (
              isDriverSeat && (
                <SvgText
                  x={coord.cx}
                  y={coord.cy}
                  fontSize="20"
                  fill="white"
                  textAnchor="middle"
                  alignmentBaseline="central"
                  fontWeight="bold"
                >
                  D
                </SvgText>
              )
            )}
            {/* Companion badge overlay */}
            {isCompanion && (
              <>
                <Circle
                  cx={coord.cx + radius * 0.6}
                  cy={coord.cy - radius * 0.6}
                  r={radius * 0.38}
                  fill="#616161"
                />
                <SvgText
                  x={coord.cx + radius * 0.6}
                  y={coord.cy - radius * 0.6}
                  fontSize="14"
                  fill="white"
                  textAnchor="middle"
                  alignmentBaseline="central"
                  fontWeight="bold"
                >
                  A
                </SvgText>
              </>
            )}
          </React.Fragment>
        );
      })}
    </Svg>
  );
}

function VanSVG({
  seats, seatAssignments, onSeatPress, color, selectedSeat, driverSeat,
}) {
  const seatLayout = [
    // front: driver, passenger
    { cx: 50, cy: 100 }, { cx: 115, cy: 100 }, { cx: 180, cy: 100 },
    // 2nd row
    { cx: 50, cy: 250 }, { cx: 115, cy: 250 }, { cx: 180, cy: 250 },
    // 3rd row
    { cx: 50, cy: 400 }, { cx: 115, cy: 400 }, { cx: 180, cy: 400 },
  ];

  const seatCoordinates = seatLayout.slice(0, seats);
  const radius = 30;

  return (
    <Svg width="170" height="280" viewBox="0 0 230 472.43204">
      <Defs>
        {seatCoordinates.map((coord, index) => (
          <ClipPath key={`clip-${index}`} id={`clip-van-${index}`}>
            <Circle cx={coord.cx} cy={coord.cy} r={radius} />
          </ClipPath>
        ))}
      </Defs>
      <CustomVanSVG color={color} />
      {seatCoordinates.map((coord, index) => {
        const seatNumber = index + 1;
        const assignment = seatAssignments?.find((a) => a.seatNumber === seatNumber);
        const isDriverSeat = seatNumber === driverSeat;
        const seatColor = getSeatColor(seatNumber, assignment, selectedSeat, driverSeat);
        const hasAvatar = assignment?.player?.profile?.pictureThumbnail;
        const isCompanion = !isDriverSeat && assignment?.isCompanion;

        return (
          <React.Fragment key={index}>
            <Circle
              cx={coord.cx}
              cy={coord.cy}
              r={radius}
              fill={seatColor}
              onPress={() => onSeatPress(seatNumber, assignment)}
            />
            {hasAvatar && !isDriverSeat ? (
              <Image
                x={coord.cx - radius}
                y={coord.cy - radius}
                width={radius * 2}
                height={radius * 2}
                href={assignment.player.profile.pictureThumbnail}
                clipPath={`url(#clip-van-${index})`}
                preserveAspectRatio="xMidYMid slice"
                onPress={() => onSeatPress(seatNumber, assignment)}
              />
            ) : assignment ? (
              <SvgText
                x={coord.cx}
                y={coord.cy}
                fontSize="10"
                fill="white"
                textAnchor="middle"
                alignmentBaseline="central"
                fontWeight="bold"
              >
                {isDriverSeat
                  ? 'D'
                  : getInitials(assignment.player.firstName, assignment.player.lastName)}
              </SvgText>
            ) : (
              isDriverSeat && (
                <SvgText
                  x={coord.cx}
                  y={coord.cy}
                  fontSize="10"
                  fill="white"
                  textAnchor="middle"
                  alignmentBaseline="central"
                  fontWeight="bold"
                >
                  D
                </SvgText>
              )
            )}
            {/* Companion badge overlay */}
            {isCompanion && (
              <>
                <Circle
                  cx={coord.cx + radius * 0.6}
                  cy={coord.cy - radius * 0.6}
                  r={radius * 0.38}
                  fill="#616161"
                />
                <SvgText
                  x={coord.cx + radius * 0.6}
                  y={coord.cy - radius * 0.6}
                  fontSize="10"
                  fill="white"
                  textAnchor="middle"
                  alignmentBaseline="central"
                  fontWeight="bold"
                >
                  A
                </SvgText>
              </>
            )}
          </React.Fragment>
        );
      })}
    </Svg>
  );
}

function TransportationVisualizerWithAssignments({
  type,
  seats,
  color,
  seatAssignments = [],
  onSeatPress,
  selectedSeat,
  driverSeat = 1,
}) {
  const renderVehicle = () => {
    switch (type?.toUpperCase()) {
      case 'CAR':
        return (
          <CarSVG
            seats={seats}
            seatAssignments={seatAssignments}
            onSeatPress={onSeatPress}
            color={color}
            selectedSeat={selectedSeat}
            driverSeat={driverSeat}
          />
        );
      case 'VAN':
        return (
          <VanSVG
            seats={seats}
            seatAssignments={seatAssignments}
            onSeatPress={onSeatPress}
            color={color}
            selectedSeat={selectedSeat}
            driverSeat={driverSeat}
          />
        );
      default:
        return (
          <CarSVG
            seats={seats}
            seatAssignments={seatAssignments}
            onSeatPress={onSeatPress}
            color={color}
            selectedSeat={selectedSeat}
            driverSeat={driverSeat}
          />
        );
    }
  };

  return (
    <View style={{ alignItems: 'center', marginVertical: 10 }}>
      {renderVehicle()}

      {/* Legend */}
      <View
        style={{
          marginTop: 16,
          flexDirection: 'row',
          justifyContent: 'space-around',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', margin: 4 }}>
          <View
            style={{
              width: 16,
              height: 16,
              backgroundColor: 'grey',
              borderRadius: 8,
              marginRight: 8,
            }}
          />
          <Text style={{ fontSize: 12 }}>Disponible</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', margin: 4 }}>
          <View
            style={{
              width: 16,
              height: 16,
              backgroundColor: '#4CAF50',
              borderRadius: 8,
              marginRight: 8,
            }}
          />
          <Text style={{ fontSize: 12 }}>Assigné</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', margin: 4 }}>
          <View
            style={{
              width: 16,
              height: 16,
              backgroundColor: '#2E7D32',
              borderRadius: 8,
              marginRight: 8,
            }}
          />
          <Text style={{ fontSize: 12 }}>Conducteur</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', margin: 4 }}>
          <View
            style={{
              width: 16,
              height: 16,
              backgroundColor: '#616161',
              borderRadius: 8,
              marginRight: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={{ fontSize: 9, color: 'white', fontWeight: 'bold' }}>A</Text>
          </View>
          <Text style={{ fontSize: 12 }}>Accompagnateur</Text>
        </View>
      </View>
    </View>
  );
}

export default TransportationVisualizerWithAssignments;
